import { useGlobalStore } from '@/stores/global';
import axios, { AxiosError, type AxiosPromise, type AxiosRequestConfig } from 'axios';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

axios.defaults.headers.common = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export async function configureAxiosInterceptor(route: RouteLocationNormalizedLoaded) {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Axios error status 0 is for a request cancelled.
      // This test is made to bypass CORS issue when tokens incorrect.
      // In case this fix causes side effects, please check this:
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#requests_with_credentials
      if (axios.isCancel(error)) {
        return;
      }

      const globalStore = useGlobalStore();

      // Check for public route
      if (route.meta.group === 'public' && error?.response) {
        const { status } = error.response;

        if (status === 403) {
          document.location.href = '/403';
        } else if (status === 404) {
          document.location.href = '/404';
        } else if (status === 503) {
          document.location.href = globalStore.login_url;
        }
      }

      // Redirect to auth
      if (
        error?.response &&
        [0, 401, 503].includes(error.response.status) &&
        route.meta.group !== 'public'
      ) {
        const currentUrl = encodeURIComponent(window.location.href);
        document.location.href = `${globalStore.login_url}?forceRedirect=${currentUrl}`;
      }

      return Promise.reject(error);
    },
  );
}

function makeRequest<T>(options: AxiosRequestConfig): AxiosPromise<T> {
  const globalStore = useGlobalStore();
  const { api_url } = globalStore;

  return axios({
    ...options,
    withCredentials: true,
    url: `${api_url}${options.url}`,
    validateStatus: (status) => status < 400,
  });
}

export async function auth(options: { email: string }) {
  const globalStore = useGlobalStore();
  const { api_url, oauth_token } = globalStore;
  const { email } = options;

  return axios
    .post(`${api_url}/auth/oauth`, {
      email,
      token: oauth_token,
    })
    .then((response) => response.data)
    .catch((err) => console.warn(err));
}

export default async function <T>(options: AxiosRequestConfig) {
  try {
    const res = await makeRequest<T>(options);
    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    if (!(error instanceof Error)) {
      return;
    }

    // Redirect to auth
    if (error instanceof AxiosError && [0, 401, 503].includes(error.response?.status || 0)) {
      const globalStore = useGlobalStore();
      const currentUrl = encodeURIComponent(window.location.href);

      if (!currentUrl.includes('%2Fpublic%2F')) {
        document.location.href = `${globalStore.login_url}?forceRedirect=${currentUrl}`;
      }
    }

    // Enrich error message with context
    if (error instanceof AxiosError) {
      const context = options.url?.replace(/([0-9a-fA-F]{32})/, 'uuid');
      const statusCode = error.response?.status;

      // Enrich error message with context
      error.message = `${error.message} [${context}${statusCode ? ` - ${statusCode}` : ''}]`;
    }

    // Log in dev
    if (process.env.NODE_ENV === 'development') {
      console.error(error);
    }

    throw error;
  }
}
